/**
 * Ear-training tone player: sounds a single swara or a short phrase of swaras
 * over the tanpura drone, for pitch-identification questions (ear-training
 * plan, Task 2). This module is audio-only — no UI/DOM/CSS. It mirrors the
 * `AudioContext` lifecycle conventions used by `metronome.ts`/`engine.ts`
 * (lazily construct, `await ctx.resume()`, tear down on `stop`), but the
 * context here is created lazily on the *first play call* rather than an
 * explicit `start()` — `createTonePlayer()` itself must be safely callable
 * (and unit-testable) without a real Web Audio implementation available.
 */

const DEFAULT_DURATION_MS = 700;
const ATTACK_S = 0.02; // 20ms soft attack — avoids a click at note-on
const RELEASE_S = 0.09; // 90ms release — avoids a click at note-off
const PEAK_GAIN = 0.35; // a single clear tone; comfortably below clipping

/** Convert a cents offset from Sa into an absolute frequency (12-TET-agnostic —
 * cents are continuous, so this works for just-intonation swara cents too). */
export function centsToFreq(saHz: number, cents: number): number {
  return saHz * 2 ** (cents / 1200);
}

/** Additive-partial stack for the tone's timbre: fundamental + 2nd/3rd/4th
 * harmonics at decreasing weights, giving a warm, flute/reed-ish sustained
 * note instead of a bare, machine-like sine — while the fundamental stays
 * dominant, so the perceived pitch (the whole point of a pitch-ID quiz)
 * remains completely unambiguous. */
const TONE_PARTIAL_MULTIPLES = [1, 2, 3, 4];
const TONE_PARTIAL_WEIGHTS = [1.0, 0.5, 0.3, 0.15];

/** Normalizes a set of partial weights so they sum to `peak` — used to size
 * each partial's individual gain within the additive stack so the stack's
 * combined amplitude lands at `peak` regardless of how many partials/weights
 * are combined, rather than summing past it. A pure helper (no Web Audio),
 * kept separate from `scheduleNote` so this bit of the timbre is unit-
 * testable without a real `AudioContext`. */
export function normalizedPartialGains(weights: number[], peak: number): number[] {
  const sum = weights.reduce((total, w) => total + w, 0);
  if (sum <= 0) return weights.map(() => 0);
  return weights.map((w) => (w / sum) * peak);
}

export interface TonePlayer {
  /** Play one swara at `saHz * 2**(cents/1200)` for `durationMs` (default ~700ms). */
  playSwara(saHz: number, cents: number, durationMs?: number): Promise<void>;
  /** Play a sequence of swaras back-to-back, each with its own envelope. */
  playPhrase(saHz: number, notes: { cents: number; durationMs?: number }[]): Promise<void>;
  /** Cancel anything scheduled and ramp down cleanly. Safe to call anytime. */
  stop(): void;
  /** Eagerly create + `resume()` the shared `AudioContext`, reusing the same
   * promise-cache as `playSwara`/`playPhrase` (see `ensureContext`) — call
   * this SYNCHRONOUSLY at the very top of a user-gesture handler, before any
   * `await`. Mobile browsers (notably iOS Safari) only grant an
   * `AudioContext` its "running" state when it's created/resumed inside that
   * synchronous user-activation window; by the time ear-training's first
   * `playSwara` call happens (several `await`s after the tap, e.g. after
   * `await ensureDrone()`), that window has already closed and the context
   * would stay `suspended` — audible drone, silent tone. Calling `unlock()`
   * first, before those awaits, gets the context running while the gesture
   * is still "live". Never throws — a failure here just means the later
   * `playSwara`/`playPhrase` call falls back to its own (also fail-soft)
   * `ensureContext()` attempt. */
  unlock(): void;
}

/** One note's timing, resolved from a possibly-omitted `durationMs`. */
interface ResolvedNote {
  cents: number;
  durationMs: number;
}

export function createTonePlayer(): TonePlayer {
  let ctx: AudioContext | undefined;
  let master: GainNode | undefined;
  // Caches the in-flight *construction* promise (not just the resolved
  // AudioContext) so two calls that both arrive before the first `new
  // AudioContext()`/`resume()` settles (e.g. a double-tap on the very first
  // play) await the same context instead of each constructing their own —
  // the loser of a resolved-value race would otherwise leak silently.
  let ctxPromise: Promise<AudioContext> | undefined;
  // Currently-sounding note groups, tracked so `stop()` can ramp them all
  // down and cancel anything still scheduled ahead of playback time. Each
  // entry is one note's whole additive-partial stack (`oscs`) sharing one
  // envelope gain (`gain`) — see `scheduleNote`.
  const active = new Set<{ oscs: OscillatorNode[]; gain: GainNode }>();
  // Resolvers for `playSwara`/`playPhrase` calls currently awaiting their
  // last note's completion. `stop()` invokes and clears these immediately so
  // an in-flight play promise settles as soon as playback is cut, rather than
  // waiting out the note's original (now-silenced) duration.
  const pendingCompletions = new Set<() => void>();

  /** Lazily create (or resume) the shared AudioContext. Never throws
   * synchronously — if construction/resume fails (e.g. called before any
   * user gesture), the returned promise rejects so callers can handle it. */
  async function ensureContext(): Promise<AudioContext> {
    if (!ctxPromise) {
      ctxPromise = (async () => {
        const created = new AudioContext();
        await created.resume();
        const gain = created.createGain();
        gain.gain.value = 1;
        gain.connect(created.destination);
        ctx = created;
        master = gain;
        return created;
      })().catch((err: unknown) => {
        // Construction failed (e.g. no user gesture yet) — clear the cache so
        // a later call (after a real gesture) gets a fresh attempt instead of
        // being stuck replaying this rejection forever.
        ctxPromise = undefined;
        throw err;
      });
    }
    const resolved = await ctxPromise;
    if (resolved.state === 'suspended') await resolved.resume();
    return resolved;
  }

  /** Schedule one note as an additive-partial stack (fundamental + a few
   * harmonics, see `TONE_PARTIAL_MULTIPLES`/`TONE_PARTIAL_WEIGHTS` above)
   * sharing a single attack/hold/release envelope, starting at `when` (an
   * AudioContext time) and lasting `durationMs`. Replaces a single bare-sine
   * oscillator with a warmer, more musical timbre while keeping the
   * fundamental (and thus the pitch) unambiguous. */
  function scheduleNote(
    audioCtx: AudioContext,
    freq: number,
    when: number,
    durationMs: number,
  ): { entry: { oscs: OscillatorNode[]; gain: GainNode }; end: number } {
    const gain = audioCtx.createGain();
    // Each partial gets its own oscillator + fixed-ratio gain, all summed
    // into the shared envelope `gain` node below — so the envelope (and
    // `stop()`'s ramp-down) only needs to touch one node, not N.
    const partialGains = normalizedPartialGains(TONE_PARTIAL_WEIGHTS, 1);
    const oscs = TONE_PARTIAL_MULTIPLES.map((multiple, i) => {
      const osc = audioCtx.createOscillator();
      const partialGain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq * multiple;
      partialGain.gain.value = partialGains[i]!;
      osc.connect(partialGain);
      partialGain.connect(gain);
      return osc;
    });

    const durationS = durationMs / 1000;
    const releaseS = Math.min(RELEASE_S, durationS / 2);
    const attackS = Math.min(ATTACK_S, durationS / 2);
    const holdEnd = Math.max(when + attackS, when + durationS - releaseS);
    const end = when + durationS;

    // Soft attack -> hold -> soft release, entirely via gain ramps so the
    // waveform never starts/stops mid-cycle (no clicks/pops).
    gain.gain.setValueAtTime(0, when);
    gain.gain.linearRampToValueAtTime(PEAK_GAIN, when + attackS);
    gain.gain.setValueAtTime(PEAK_GAIN, holdEnd);
    gain.gain.linearRampToValueAtTime(0, end);

    gain.connect(master ?? audioCtx.destination);

    const entry = { oscs, gain };
    active.add(entry);
    // All partials share the same start/stop times, so they end together —
    // only the fundamental's `onended` is needed to drive cleanup (and, in
    // `playNotes` below, completion); wiring every partial would just run
    // the same cleanup N times over.
    const fundamental = oscs[0]!;
    fundamental.onended = () => {
      active.delete(entry);
      for (const partialOsc of oscs) partialOsc.disconnect();
      gain.disconnect();
    };

    for (const partialOsc of oscs) {
      partialOsc.start(when);
      partialOsc.stop(end);
    }
    return { entry, end };
  }

  function resolveNote(note: { cents: number; durationMs?: number }): ResolvedNote {
    return note.durationMs === undefined
      ? { cents: note.cents, durationMs: DEFAULT_DURATION_MS }
      : { cents: note.cents, durationMs: note.durationMs };
  }

  async function playNotes(saHz: number, notes: ResolvedNote[]): Promise<void> {
    const audioCtx = await ensureContext();
    let when = audioCtx.currentTime;
    let lastEntry: { oscs: OscillatorNode[]; gain: GainNode } | undefined;
    for (const note of notes) {
      const freq = centsToFreq(saHz, note.cents);
      const scheduled = scheduleNote(audioCtx, freq, when, note.durationMs);
      when = scheduled.end;
      lastEntry = scheduled.entry;
    }
    if (!lastEntry) return;
    const finalEntry = lastEntry;
    // Resolve off the last note's *actual* completion (its real 'ended'
    // event, on the AudioContext clock) rather than a wall-clock setTimeout —
    // a background-tab-throttled timer would drift from real playback. Also
    // register with `pendingCompletions` so `stop()` can settle this promise
    // immediately if playback is cut short, instead of leaving the caller
    // blocked until the (now-silenced) note would have naturally ended.
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        pendingCompletions.delete(finish);
        resolve();
      };
      pendingCompletions.add(finish);
      finalEntry.oscs[0]!.addEventListener('ended', finish, { once: true });
    });
  }

  return {
    async playSwara(saHz, cents, durationMs) {
      const resolved: ResolvedNote =
        durationMs === undefined
          ? { cents, durationMs: DEFAULT_DURATION_MS }
          : { cents, durationMs };
      try {
        await playNotes(saHz, [resolved]);
      } catch (err) {
        // No user gesture yet / AudioContext unavailable — fail soft (resolve,
        // don't throw) so callers (UI) can retry after a gesture instead of
        // crashing, but still surface genuine failures instead of hiding them
        // as silent "no sound, no error".
        console.warn('TonePlayer.playSwara: audio could not be played', err);
      }
    },

    async playPhrase(saHz, notes) {
      try {
        await playNotes(saHz, notes.map(resolveNote));
      } catch (err) {
        // Same fail-soft-but-visible guard as playSwara.
        console.warn('TonePlayer.playPhrase: audio could not be played', err);
      }
    },

    stop() {
      // Settle any in-flight play promises right away — playback is about to
      // be silenced, so callers awaiting `playSwara`/`playPhrase` shouldn't
      // block until the original (now-moot) note duration would have elapsed.
      for (const finish of [...pendingCompletions]) finish();
      pendingCompletions.clear();

      const audioCtx = ctx;
      if (!audioCtx) return;
      const now = audioCtx.currentTime;
      for (const { oscs, gain } of active) {
        try {
          gain.gain.cancelScheduledValues(now);
          gain.gain.setValueAtTime(gain.gain.value, now);
          gain.gain.linearRampToValueAtTime(0, now + RELEASE_S);
        } catch {
          // Gain node may already be disconnected; nothing left to ramp.
        }
        // Each partial gets its own try/catch — an oscillator that's already
        // stopped (e.g. `onended` from a prior note fired between the
        // `active` snapshot and here) throws on `.stop()`, and a single
        // shared try/catch around the whole loop would abort early and skip
        // stopping the remaining partials in this entry.
        for (const osc of oscs) {
          try {
            osc.stop(now + RELEASE_S);
          } catch {
            // Already stopped/disconnected; move on to the next partial.
          }
        }
      }
      active.clear();
    },

    unlock() {
      // Fire-and-forget: kick off (or reuse) the same cached `ensureContext()`
      // promise `playSwara`/`playPhrase` use, so this "just" gets the
      // AudioContext running eagerly. Errors are swallowed here (with a
      // warning) rather than propagated — callers invoke this synchronously
      // from a click handler and shouldn't need to handle a rejection; the
      // actual play call still has its own fail-soft catch as a backstop.
      void ensureContext().catch((err: unknown) => {
        console.warn('TonePlayer.unlock: audio context could not be unlocked', err);
      });
    },
  };
}
